import type {CommonAuthFormParams, ErrorNotificationData, LoginPageState, OauthFormBaseParams, RecaptchaTypes, VoidFunction} from '@Components/login-page/login-page.types';
import {PASSWORD_POLICY_ITEM_STATUS, PASSWORD_STATUS} from '@Components/signup-form/signup-form.types';
import {isIOSApp, isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {openErrorModal} from '@Modals/error-modal';
import {MIN_PASSWORD_LENGTH} from '@Libraries/user.library';

/**
 * Library containing helper functions for the login-signup page and auth-modal
 */
export enum AUTH_TYPE {
  LOGIN = 'login',
  SIGNUP = 'signup',
  SIGNUP_PASSWORD = 'signup_password',
  STUDENT_LOGIN = 'student_login',
  CONFIRM_LOGIN = 'confirm_login',
  FORGOT_PASSWORD = 'forgot_password',
  INACTIVE_PASSWORD_RESET = 'inactive_password_reset',
  TWO_FACTOR_VERIFICATION = 'two_factor_verification',
  SAVED_ACCOUNTS = 'saved_accounts',
  SAVED_ACCOUNT_PASSWORD = 'saved_account_password',
}

/**
 * Where the modal was opened from. Each context pairs the copy that names what the user was doing with
 * the artwork beside it, so a caller cannot put one flow's title next to another's image. `GENERAL` is
 * the default dialogue and what every caller gets unless it says otherwise. Lives here rather than
 * with the panel because `poster-whiteboard` opens the modal too and may only import domain values from
 * `@Libraries/*`.
 */
export enum AuthContext {
  GENERAL = 'general',
  EMAIL_MAKER = 'email_maker',
  EVENT_SHARING = 'event_sharing',
  SOCIAL_PUBLISHING = 'social_publishing',
  SIGNUP_FORM_MAKER = 'signup_form_maker',
  EVENT_TICKETING = 'event_ticketing',
  EVENT_CO_PROMOTION = 'event_co_promotion',
  AI_CHAT = 'ai_chat',
  SAVE_DESIGN = 'save_design',
}

/** How a saved account last logged in — mirrors the PHP `SavedLoginMethod` in the `pmwsa` cookie (§32). */
export enum SavedLoginMethod {
  WEB = 'web',
  GOOGLE = 'google',
  APPLE = 'apple',
  FACEBOOK = 'facebook',
}

/** The server keeps five accounts; the desktop login page lists them all, the auth modal lists the most recent three (D61). */
export const MAX_SAVED_LOGIN_ACCOUNTS_IN_AUTH_MODAL = 3;
/** Phone and tablet widths list the most recent three. */
export const MAX_SAVED_LOGIN_ACCOUNTS_ON_MOBILE_AND_TABLET = 3;

/** Written by the OAuth callback page once Google or Facebook answers; the window that opened the consent flow polls it, then clears it. */
export const SSO_OAUTH_DATA_COOKIE = 'pmwsac';
export const SSO_OAUTH_COOKIE_POLL_INTERVAL_MS = 500;

export interface SavedLoginAccount {
  hashedId: string;
  name: string;
  email: string;
  method: SavedLoginMethod;
  /** Which of the three avatar colours this account holds; assigned by the server when it is saved. */
  colourSlot: number;
}

/** Every auth dialog has 8px corners; the modal shell's default is 4. */
export const AUTH_DIALOG_MODAL_BORDER_RADIUS = 8;

export enum AUTH_SUB_TYPE {
  LOGIN_WITH_FORM = 'login_with_form',
  LOGIN_WITHOUT_FORM = 'login_without_form',
  SIGNUP_WITH_FORM = 'signup_with_form',
  SIGNUP_WITHOUT_FORM = 'signup_without_form',
}

export enum AUTH_MODE {
  ASYNC = 'async',
  DEFAULT = 'default',
}

export enum LOGIN_OPTIONS {
  DEFAULT = '0',
  ALL = '1',
  OAUTH = '2',
}

export const CONFIRM_LOGIN_URL = 'authenticate/confirmlogin';

export const EMAIL_PLACEHOLDER = 'amy@postermywall.com';
export const PASSWORD_PLACEHOLDER = '●●●●●●●●●●';

const MOBILE_SCREEN_MAX_WIDTH = 768;

/** Paired with `$phone-width` in `login-page-util.module.scss`; below it the card takes its phone dimensions. */
const PHONE_SCREEN_MAX_WIDTH = 480;

/** Paired with `$widescreen-breakpoint` in `login-page-util.module.scss`; the login page alone uses it. */
const LOGIN_PAGE_WIDESCREEN_MIN_WIDTH = 1600;

/** Below this the log-in providers reflow into two columns. */
const LOGIN_PAGE_SHORT_VIEWPORT_MAX_HEIGHT = 720;

const ABSOLUTE_URL_PATTERN = /^https?:\/\//;

let authFormParams: CommonAuthFormParams = {};
let oauthFormParams: OauthFormBaseParams = {};
let recaptchaSelector: JQuery | null = null;

export const initializeAuthFormParams = (params: CommonAuthFormParams) => {
  authFormParams = params;
};

export const getCommonAuthFormParams = (): CommonAuthFormParams => {
  return authFormParams;
};

export const initializeoAuthFormParams = (params: OauthFormBaseParams) => {
  oauthFormParams = params;
};

export const getoAuthFormParams = (): OauthFormBaseParams => {
  return oauthFormParams;
};

export const setRecaptchaSelector = (selector: JQuery) => {
  recaptchaSelector = selector;
};

export const getRecaptchaSelector = () => {
  return recaptchaSelector;
};

export const getRecaptchaSelectorValue = (): RecaptchaTypes => {
  return recaptchaSelector ? recaptchaSelector.val() : '';
};

export const redirectToPage = (url?: string): void => {
  window.location.href = url || window.PMW.util.site_url('posters/mine');
};

/**
 * Builds the absolute URL to send a freshly logged-in user to, moving them onto the domain/subfolder
 * of their preferred language when the login response provides one.
 */
export const getPostLoginRedirectUrl = (redirectUri: string, preferredLanguage?: string): string => {
  // oauth pendingMFA responses store an absolute redirect URL; everything else is a URI relative to site_url
  const redirectUrl = ABSOLUTE_URL_PATTERN.test(redirectUri) ? redirectUri : window.PMW.util.site_url(redirectUri);

  if (!preferredLanguage) {
    return redirectUrl;
  }

  return window.PMW.util.updateLanguageDomainForUrl(redirectUrl, preferredLanguage);
};

export const showErrors = (state: LoginPageState, errorMessage: string) => {
  const errorData: ErrorNotificationData = {
    errorMessage,
    showError: true,
    animateNotificationBanner: true,
  };

  updateErrors(state, errorData);
};

export const hideErrors = (state: LoginPageState) => {
  const errorData: ErrorNotificationData = {
    errorMessage: '',
    showError: false,
    animateNotificationBanner: false,
  };

  updateErrors(state, errorData);
};

const updateErrors = (state: LoginPageState, updatedState: ErrorNotificationData) => {
  state.errorMessage = updatedState.errorMessage;
  state.showError = updatedState.showError;
  state.areFieldsMarkedInvalid = updatedState.showError;
  state.animateNotificationBanner = updatedState.animateNotificationBanner;
};

export const getPasswordFieldStatus = (password: string, passwordHasFocus: boolean): PASSWORD_STATUS => {
  if (password.length === 0 || passwordHasFocus || isPasswordPolicySatisfied(password, passwordHasFocus)) {
    return PASSWORD_STATUS.DEFAULT;
  }

  return PASSWORD_STATUS.ERROR;
};

export const getConfirmPasswordStatus = (password: string, confirmPassword: string, passwordHasFocus: boolean, confirmPasswordHasFocus: boolean): PASSWORD_STATUS => {
  if (confirmPassword.length === 0 || confirmPasswordHasFocus) {
    return PASSWORD_STATUS.DEFAULT;
  }

  if (!isPasswordPolicySatisfied(password, passwordHasFocus) || password.length === 0) {
    return PASSWORD_STATUS.DEFAULT;
  }

  return confirmPassword === password ? PASSWORD_STATUS.DEFAULT : PASSWORD_STATUS.ERROR;
};

const isPasswordPolicySatisfied = (password: string, passwordHasFocus: boolean) => {
  return (
    getStatusForPasswordLength(password, passwordHasFocus) === PASSWORD_POLICY_ITEM_STATUS.COMPLETE &&
    getStatusForSpecialCharactersInPassword(password, passwordHasFocus) === PASSWORD_POLICY_ITEM_STATUS.COMPLETE
  );
};

/**
 * A rule the user has not met reads as pending while they type and as failed once they move on. A rejected
 * submit counts as moving on, so an empty submit turns the rules red instead of doing nothing.
 */
export const getPolicyItemStatusAfterRejectedSubmit = (statusWhileEditing: PASSWORD_POLICY_ITEM_STATUS): PASSWORD_POLICY_ITEM_STATUS => {
  return statusWhileEditing === PASSWORD_POLICY_ITEM_STATUS.COMPLETE ? PASSWORD_POLICY_ITEM_STATUS.COMPLETE : PASSWORD_POLICY_ITEM_STATUS.INCOMPLETE;
};

export const getStatusForPasswordLength = (password: string, passwordHasFocus: boolean): PASSWORD_POLICY_ITEM_STATUS => {
  if (password.length >= MIN_PASSWORD_LENGTH) {
    return PASSWORD_POLICY_ITEM_STATUS.COMPLETE;
  }

  if (password.length === 0 || passwordHasFocus) {
    return PASSWORD_POLICY_ITEM_STATUS.IN_PROGRESS;
  }

  return PASSWORD_POLICY_ITEM_STATUS.INCOMPLETE;
};

export const getStatusForSpecialCharactersInPassword = (password: string, passwordHasFocus: boolean) => {
  const containsSpecialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
  const containsNumberRegex = /\d/;

  if (password.match(containsSpecialCharRegex) || password.match(containsNumberRegex)) {
    return PASSWORD_POLICY_ITEM_STATUS.COMPLETE;
  }

  if (password.length === 0 || passwordHasFocus) {
    return PASSWORD_POLICY_ITEM_STATUS.IN_PROGRESS;
  }

  return PASSWORD_POLICY_ITEM_STATUS.INCOMPLETE;
};

export const handleAuthFormSubmit = async (cb: VoidFunction, token: string) => {
  const inputRecaptcha = getRecaptchaSelector();
  if (inputRecaptcha) {
    inputRecaptcha.val(token);
    cb();
  }
};

/**
 * The login page's container is the one reliable marker that the redesigned auth panels are on screen —
 * `authMode` stays DEFAULT on other pages that render the same forms, such as reset password.
 */
export const isLoginPageMounted = (): boolean => {
  return document.getElementById('login-sign-up-page') !== null;
};

export const doesAuthTypeNeedBackButton = (type: AUTH_TYPE): boolean => {
  return type === AUTH_TYPE.STUDENT_LOGIN || type === AUTH_TYPE.FORGOT_PASSWORD || type === AUTH_TYPE.CONFIRM_LOGIN || type === AUTH_TYPE.TWO_FACTOR_VERIFICATION;
};

export const areThirdPartyButtonsNeededForType = (type: AUTH_TYPE): boolean => {
  return isLoginOrSignupAuthType(type);
};

export const isOAuthFlow = (loginOptionsMode: string): boolean => {
  return loginOptionsMode === LOGIN_OPTIONS.OAUTH;
};

export const onFacebookConnect = (redirectUrl: string, status?: boolean): void => {
  if (status) {
    let url = redirectUrl;
    if (window.PMW.USER_PREFERRED_LANGUAGE) {
      url = window.PMW.util.updateLanguageDomainForUrl(redirectUrl, window.PMW.USER_PREFERRED_LANGUAGE);
    }
    redirectToPage(url);
  }
};

export const getOauthUrl = async (uri: string): Promise<string> => {
  const url = (await window.PMW.readLocal(uri, {
    isIOSAppOrEmbeddedEditor: isIOSAppOrEmbeddedEditor(),
  })) as string;
  if (!url) {
    openErrorModal();
    throw new Error('Invalid oauthUrl');
  }
  return url;
};

export const openLoginWindow = async (getAuthUrl: () => Promise<string>, onLoginUrlFetched: (url: string) => void): Promise<Window | null> => {
  if (isIOSApp()) {
    const url = await getAuthUrl();
    onLoginUrlFetched(url);
    window.webkit?.messageHandlers?.openInSafariView?.postMessage({url});
    return null;
  }
  const loginWindow = window.open('about:blank', '_blank', getOauthWindowProps());
  if (!loginWindow) {
    openErrorModal();
    return null;
  }
  const url = await getAuthUrl();
  onLoginUrlFetched(url);
  loginWindow.location.href = url;
  return loginWindow;
};

export const dismissIOSSafariView = (): void => {
  if (isIOSApp()) {
    window.webkit?.messageHandlers?.dismissSafariView?.postMessage({} as Record<string, never>);
  }
};

export const getOauthWindowProps = (): string => {
  const LOGIN_WINDOW_WIDTH = 500;
  const LOGIN_WINDOW_HEIGHT = 570;
  const screenLeft = window.screenX || window.screenLeft;
  const screenTop = window.screenY || window.screenTop;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  const left = screenLeft + (screenWidth - LOGIN_WINDOW_WIDTH) / 2;
  const top = screenTop + (screenHeight - LOGIN_WINDOW_HEIGHT) / 2;
  return `width=${LOGIN_WINDOW_WIDTH},height=${LOGIN_WINDOW_HEIGHT},left=${left},top=${top}`;
};

/**
 * The OAuth panels name the app being connected to, falling back to generic copy when it is unknown. The name can
 * come straight from the request URL and the message renders as HTML, so it is always escaped.
 */
export const getOAuthClientMessage = (type: AUTH_TYPE, clientName: string): string => {
  const options = {clientName, interpolation: {escapeValue: true}};
  if (type === AUTH_TYPE.LOGIN) {
    return clientName ? window.i18next.t('pmwjs_oauth_login_page_client_message', options) : window.i18next.t('pmwjs_oauth_login_page_default_message');
  }
  return clientName ? window.i18next.t('pmwjs_oauth_signup_page_client_message', options) : window.i18next.t('pmwjs_oauth_signup_page_default_message');
};

export const isMobileScreen = (width: number): boolean => {
  return width <= MOBILE_SCREEN_MAX_WIDTH;
};

export const isPhoneScreen = (width: number): boolean => {
  return width <= PHONE_SCREEN_MAX_WIDTH;
};

export const isLoginPageWidescreen = (width: number): boolean => {
  return width >= LOGIN_PAGE_WIDESCREEN_MIN_WIDTH;
};

export const isLoginPageShortViewport = (height: number): boolean => {
  return height < LOGIN_PAGE_SHORT_VIEWPORT_MAX_HEIGHT;
};

export const isLoginAuthType = (type: AUTH_TYPE): boolean => {
  return type === AUTH_TYPE.LOGIN;
};

export const isSignupAuthType = (type: AUTH_TYPE): boolean => {
  return type === AUTH_TYPE.SIGNUP;
};

export const isInactivePasswordResetAuthType = (type: AUTH_TYPE): boolean => {
  return type === AUTH_TYPE.INACTIVE_PASSWORD_RESET;
};

export const isLoginOrSignupAuthType = (type: AUTH_TYPE): boolean => {
  return type === AUTH_TYPE.LOGIN || type === AUTH_TYPE.SIGNUP;
};

export const isLoginOrSignupFormVisible = (subtype: AUTH_SUB_TYPE): boolean => {
  return subtype === AUTH_SUB_TYPE.LOGIN_WITH_FORM || subtype === AUTH_SUB_TYPE.SIGNUP_WITH_FORM;
};

/** The `authenticate/savedAccounts` payload: the records the server unsealed from the httpOnly `pmwsa` cookie. */
interface SavedLoginAccountsResponse {
  accounts: SavedLoginAccount[];
}

/**
 * The accounts the server remembered on this device, for the auth modal, which opens on pages the server did not
 * render with them. Capped to the modal's three; the embedded editor never offers them (D60); a failed request
 * falls back to the standard panels rather than blocking the modal.
 */
export const readSavedLoginAccountsForAuthModal = async (): Promise<SavedLoginAccount[]> => {
  if (window.PMW.isEmbedded()) {
    return [];
  }
  try {
    const response = (await window.PMW.readLocal('authenticate/savedAccounts')) as SavedLoginAccountsResponse;
    return response.accounts.slice(0, MAX_SAVED_LOGIN_ACCOUNTS_IN_AUTH_MODAL);
  } catch {
    return [];
  }
};

/** The accounts the login page was rendered with, serialised into its hidden input by the server. */
export const readSavedLoginAccountsFromPage = (serializedAccounts: string): SavedLoginAccount[] => {
  try {
    const accounts: unknown = JSON.parse(serializedAccounts);
    return Array.isArray(accounts) ? (accounts as SavedLoginAccount[]) : [];
  } catch {
    return [];
  }
};

/** Removes the given accounts from this device's cookie; an empty list removes them all. */
export const forgetSavedLoginAccounts = async (hashedIds: string[]): Promise<void> => {
  await window.PMW.writeLocal('authenticate/forgetSavedAccounts', {hashedIds});
};
